import { useEffect, useEffectEvent, useState } from "react"
import { message } from "antd"
import type { TablePaginationConfig, TableProps } from "antd"
import { ApiRequestError } from "~/common/request"
import { isAbortError } from "~/common/utils"

export type PagedResponse<TItem> = {
  data?: { items?: TItem[] | null; total?: number | null } | null
}

type PagedQueryOptions<TFilters, TItem> = {
  // page 从 0 开始，与后端分页参数一致
  fetchPage: (
    params: TFilters & { page: number; page_size: number },
    signal: AbortSignal,
  ) => Promise<PagedResponse<TItem>>
  initialFilters: TFilters
  defaultPageSize?: number
  pageSizeOptions?: number[]
  // 网络异常等非业务错误的兜底提示；业务错误由请求层统一提示
  errorMessage?: string
}

// Staff 列表页通用的「筛选 + 分页 + 加载」状态
export const usePagedQuery = <TFilters extends object, TItem>({
  fetchPage,
  initialFilters,
  defaultPageSize = 20,
  pageSizeOptions = [20, 50, 100],
  errorMessage,
}: PagedQueryOptions<TFilters, TItem>) => {
  const [filters, setFilters] = useState(initialFilters)
  const [items, setItems] = useState<TItem[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(defaultPageSize)
  const [refreshVersion, setRefreshVersion] = useState(0)
  const [loading, setLoading] = useState(false)
  const loadPage = useEffectEvent(fetchPage)

  useEffect(() => {
    const controller = new AbortController()

    const load = async () => {
      setLoading(true)

      try {
        const response = await loadPage(
          { ...filters, page: page - 1, page_size: pageSize },
          controller.signal,
        )
        if (controller.signal.aborted) return

        setItems(response.data?.items || [])
        setTotal(response.data?.total || 0)
      } catch (error) {
        if (isAbortError(error) || controller.signal.aborted) return

        setItems([])
        setTotal(0)
        if (errorMessage && !(error instanceof ApiRequestError)) {
          void message.warning(errorMessage)
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false)
      }
    }

    void load()
    return () => controller.abort()
  }, [errorMessage, filters, page, pageSize, refreshVersion])

  // 应用新的筛选条件并回到第一页；条件不变时也会重新请求
  const search = (nextFilters: TFilters) => {
    setFilters(nextFilters)
    setItems([])
    setTotal(0)
    setPage(1)
    setRefreshVersion((version) => version + 1)
  }

  const refresh = () => setRefreshVersion((version) => version + 1)

  const tableProps = {
    dataSource: items,
    loading,
    pagination: {
      current: page,
      pageSize,
      pageSizeOptions,
      showSizeChanger: true,
      showTotal: (count, range) =>
        `显示 ${range[0]} 到 ${range[1]} 条，共 ${count} 条记录`,
      total,
    },
    onChange: (pagination: TablePaginationConfig) => {
      const nextPageSize = pagination.pageSize || defaultPageSize
      setPageSize(nextPageSize)
      setPage(nextPageSize === pageSize ? pagination.current || 1 : 1)
    },
  } satisfies Pick<
    TableProps<TItem>,
    "dataSource" | "loading" | "pagination" | "onChange"
  >

  return { filters, items, total, loading, search, refresh, tableProps }
}
